import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { ContactEditFormValues, ContactEmailRow } from "services/api/contacts/types";
import { getContactEmailsAPI, deleteContactEmailAPI } from "services/api/contacts/api";
import { initialDataWithPagination } from "services/api/consts";
import { Loader } from "ui/components/Loader";
import { Dialog } from "ui/components/Dialog";
import { TableSearch } from "ui/components/Table/TableSearch";
import { CONTACT_EDIT_EMAIL_COLUMNS } from "./consts";
import ContactEmailEditCard from "ui/modules/contacts/pages/ContactDetailPage/components/ContactDetailCard/components/ContactEmailEditCard/ContactEmailEditCard";

interface ContactEmailStepProps {
  formValues: ContactEditFormValues;
}

const ContactEmailStep: React.FC<ContactEmailStepProps> = ({ formValues: parentFormValues }) => {
  const contactId = parentFormValues.id;
  const { t } = useTranslate();
  const [emails, setEmails] = useState<ContactEmailRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editEmailId, setEditEmailId] = useState<string | null>(null);
  const [deleteEmailId, setDeleteEmailId] = useState<string | null>(null);

  const fetchEmails = useCallback(async () => {
    if (!contactId) return;
    setIsLoading(true);
    try {
      const result = await getContactEmailsAPI(initialDataWithPagination.pagination, contactId);
      setEmails(result.data);
    } catch {
      setEmails([]);
    }
    setIsLoading(false);
  }, [contactId]);

  useEffect(() => {
    if (contactId) fetchEmails();
  }, [contactId]);

  const handleRowClick = useCallback((emailId: string) => {
    setEditEmailId(emailId);
    setShowEdit(true);
  }, []);

  const handleAddNew = () => {
    setEditEmailId(null);
    setShowEdit(true);
  };

  // Klik na kantu ne smije otvoriti i uređivanje retka.
  const handleDeleteClick = useCallback((event: React.MouseEvent<HTMLDivElement>, emailId: string) => {
    event.stopPropagation();
    event.preventDefault();
    setDeleteEmailId(emailId);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!contactId || !deleteEmailId) return;
    await deleteContactEmailAPI(t, contactId, deleteEmailId);
    setDeleteEmailId(null);
    fetchEmails();
  };

  const handleEditSuccess = () => {
    setShowEdit(false);
    setEditEmailId(null);
    fetchEmails();
  };

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Email adrese {parentFormValues.contactTypeId === "C" ? "tvrtke" : "osobe"}</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Dodajte email adrese kontakta klikom na gumb <strong>"Dodaj email"</strong>.
            Nakon unosa možete nastaviti na sljedeći korak.
          </p>
          {isLoading ? (
            <Loader isLoading={true}> </Loader>
          ) : (
            <div className="px-3">
              {emails.length === 0 ? (
                <div className="max-w-md mx-auto">
                  <Button
                    variant="primary"
                    size="full"
                    startIcon="/media/icons/plus.svg"
                    children="Dodaj email"
                    onClick={handleAddNew}
                  />
                </div>
              ) : (
                <>
                  {/* Isti izgled kao PP aparati na unosu vozila: klik na redak uređuje, kanta briše. */}
                  <TableSearch
                    columns={CONTACT_EDIT_EMAIL_COLUMNS(handleDeleteClick)}
                    data={emails}
                    selectableRows={false}
                    onRowClick={handleRowClick}
                    isLoading={isLoading}
                  />
                  <div className="flex justify-end mt-3">
                    <Button variant="primary" startIcon="/media/icons/plus.svg" children="Dodaj email" onClick={handleAddNew} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <ContactEmailEditCard
        contactId={contactId ?? ""}
        emailId={editEmailId}
        show={showEdit}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEdit(false)}
        
      />
      <Dialog
        open={!!deleteEmailId}
        title="Brisanje email adrese"
        message="Jeste li sigurni da želite obrisati ovu email adresu?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteEmailId(null)}
      />
    </>
  );
};

export default memo(ContactEmailStep);
